import React, { useMemo, useState } from 'react';
import { Search, Plus, Stethoscope, Pencil, X, CheckCircle2, Clock, AlertTriangle, Sparkles, Users, Download, FileSpreadsheet } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { useFetch, Spinner } from '../lib/useFetch';
import { visiteApi, workersApi, companiesApi, sorveglianzaExportApi } from '../lib/api';
import { useCrudActions } from '../lib/useCrudActions';
import EntityFormModal from '../components/EntityFormModal';
import { visitaFields } from '../lib/entitySchemas';
import { fmtDate } from '../lib/format';
import { useAuth } from '../context/AuthContext';
import SortAZButton from '../components/SortAZButton';

/**
 * Sorveglianza sanitaria — aggregation is done server-side by
 * ``GET /api/visite/sanitario``. Each active worker gets exactly ONE status
 * (based on their most recent visit's ``scadenza``) so the KPIs align with
 * JOB81 headcount rather than the raw visit records.
 */
const KPI_META = {
  totale:      { label: 'Totale lavoratori', color: '#023F80', bg: '#e0e7ff', icon: Users },
  valida:      { label: 'Valide',            color: '#15803d', bg: '#dcfce7', icon: CheckCircle2 },
  in_scadenza: { label: 'In scadenza',       color: '#8a6608', bg: '#fef7d4', icon: Clock },
  scaduta:     { label: 'Scadute',           color: '#b91c1c', bg: '#fee2e2', icon: AlertTriangle },
  da_fare:     { label: 'Da fare',           color: '#7c3aed', bg: '#ede9fe', icon: Sparkles },
};

const KpiCard = ({ statoKey, value, active, onClick }) => {
  const m = KPI_META[statoKey];
  const Icon = m.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`sorveglianza-kpi-${statoKey}`}
      className={`rounded-2xl p-5 text-left transition-all ${active ? 'ring-2 ring-offset-2 ring-[#023F80]' : 'hover:shadow-md'}`}
      style={{ background: m.bg }}
    >
      <div className="flex items-center justify-between">
        <Icon size={16} style={{ color: m.color }} />
        {active && <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: m.color }}>Attivo</span>}
      </div>
      <div className="mt-3 text-3xl font-extrabold" style={{ color: m.color }}>{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-gray-600 mt-1">{m.label}</div>
    </button>
  );
};

const statusBadge = (stato) => {
  const map = {
    valida:      { label: 'Valida',       bg: 'bg-emerald-100', color: 'text-emerald-800' },
    in_scadenza: { label: 'In scadenza',  bg: 'bg-amber-100',   color: 'text-amber-800' },
    scaduta:     { label: 'Scaduta',      bg: 'bg-red-100',     color: 'text-red-800' },
    da_fare:     { label: 'Da fare',      bg: 'bg-violet-100',  color: 'text-violet-800' },
  };
  const m = map[stato] || { label: stato, bg: 'bg-slate-100', color: 'text-slate-700' };
  return <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold ${m.bg} ${m.color}`}>{m.label}</span>;
};

const SorveglianzaPage = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortDir, setSortDir] = useState('asc');

  const { data: sanitario, loading, reload } = useFetch(() => visiteApi.sanitario(), []);
  const { data: workers } = useFetch(() => workersApi.list(), []);
  const { data: companies } = useFetch(() => companiesApi.list(), []);
  const crud = useCrudActions({ api: visiteApi, label: 'visita', onChange: reload });

  const companyName = (id) => companies?.find((c) => c.id === id)?.name || '—';

  const items = sanitario?.items || [];
  const filtered = useMemo(() => {
    let list = items;
    if (statusFilter !== 'all') list = list.filter((r) => r.stato === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((r) => `${r.workerName} ${companyName(r.companyId)} ${r.mansione || ''} ${r.medico || ''}`.toLowerCase().includes(q));
    }
    // Sort alfabetico per cognome vero (CF italiano 16char + euristica nomi
    // italiani per gestire lavoratori con firstName/lastName invertiti nel DB).
    // Ridondante col sort backend ma resiste alla CDN che serve JSON vecchio.
    // Ordinamento alfabetico sul NOME VISIBILE (come letto in tabella),
    // stile Excel: la stringa che l'utente vede è quella che viene ordinata.
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...list].sort((a, b) => {
      const na = String(a.workerName || `${a.firstName || ''} ${a.lastName || ''}`).trim();
      const nb = String(b.workerName || `${b.firstName || ''} ${b.lastName || ''}`).trim();
      return na.localeCompare(nb, 'it', { sensitivity: 'base' }) * dir;
    });
  }, [items, statusFilter, search, companies, sortDir]);

  if (loading) return <Spinner />;

  return (
    <div className="space-y-5" data-testid="sorveglianza-page">
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard statoKey="totale"      value={sanitario?.totale} active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} />
        <KpiCard statoKey="valida"      value={sanitario?.valida} active={statusFilter === 'valida'} onClick={() => setStatusFilter('valida')} />
        <KpiCard statoKey="in_scadenza" value={sanitario?.in_scadenza} active={statusFilter === 'in_scadenza'} onClick={() => setStatusFilter('in_scadenza')} />
        <KpiCard statoKey="scaduta"     value={sanitario?.scaduta} active={statusFilter === 'scaduta'} onClick={() => setStatusFilter('scaduta')} />
        <KpiCard statoKey="da_fare"     value={sanitario?.da_fare} active={statusFilter === 'da_fare'} onClick={() => setStatusFilter('da_fare')} />
      </div>

      <p className="text-xs text-slate-500 -mt-2 pl-1">
        Soglia &quot;In scadenza&quot;: <b>{sanitario?.warn_days || 90} giorni</b> · &quot;Da fare&quot; = lavoratori attivi senza visita mai eseguita. <em>Solo le aziende con medico competente configurato compaiono in sorveglianza.</em>
      </p>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#e0e7ff] text-[#023F80] flex items-center justify-center"><Stethoscope size={20} /></div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Sorveglianza sanitaria</h2>
              <p className="text-xs text-slate-500">Situazione attuale: una posizione per lavoratore attivo (ultima visita). Ordinamento: nome visibile ({sortDir === 'asc' ? 'A→Z' : 'Z→A'}).</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <SortAZButton
              direction={sortDir}
              onToggle={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
              testId="sorveglianza-sort-az"
            />
            {!isClient && (
              <Link
                to="/riconciliazione/visite-align"
                data-testid="sorveglianza-import-excel"
                className="ro-hide inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-indigo-200 text-[#023F80] hover:bg-indigo-50"
              >
                <FileSpreadsheet size={13} /> Importa Excel
              </Link>
            )}
            <button
              onClick={() => sorveglianzaExportApi.download({ stato: statusFilter })}
              data-testid="sorveglianza-export"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-slate-200 hover:bg-slate-50"
            >
              <Download size={13} /> Esporta XLSX
            </button>
            {!isClient && (
              <button onClick={() => crud.onCreate({ stato: 'valido' })} className="ro-hide px-4 py-2 rounded-full bg-[#fef7d4] text-[#8a6608] text-xs font-bold tracking-wider hover:bg-[#E6B800] flex items-center gap-2">
                <Plus size={14} /> NUOVA VISITA
              </button>
            )}
          </div>
        </div>

        <div className="relative max-w-md mb-4">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca lavoratore, azienda, medico..." className="pl-9 h-10 border-gray-200" data-testid="sorveglianza-search" />
        </div>

        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-gray-500">
                <th className="text-left bg-gray-100 rounded-l-xl px-4 py-3">Lavoratore</th>
                <th className="text-left bg-gray-100 px-4 py-3">Azienda</th>
                <th className="text-left bg-gray-100 px-4 py-3">Mansione</th>
                <th className="text-left bg-gray-100 px-4 py-3">Ultima visita</th>
                <th className="text-left bg-gray-100 px-4 py-3">Scadenza</th>
                <th className="text-left bg-gray-100 rounded-r-xl px-4 py-3">Stato</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 500).map((r) => (
                <tr key={r.workerId} className="border-b border-gray-50 hover:bg-gray-50/60" data-testid={`sorv-row-${r.workerId}`}>
                  <td className="px-4 py-3 font-semibold text-gray-800">{r.workerName || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{companyName(r.companyId)}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{r.mansione || '—'}</td>
                  <td className="px-4 py-3 text-gray-700">{fmtDate(r.ultimaVisita) || <span className="text-slate-300 italic">nessuna</span>}</td>
                  <td className="px-4 py-3 text-gray-700">{fmtDate(r.scadenza) || <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3">{statusBadge(r.stato)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length > 500 && (
            <div className="py-3 text-center text-xs text-slate-500">Mostrati primi 500 di {filtered.length}. Affina la ricerca per vedere tutti.</div>
          )}
          {filtered.length === 0 && <div className="py-10 text-center text-sm text-gray-400">Nessuna posizione in questa categoria</div>}
        </div>
      </div>

      <EntityFormModal
        open={crud.open}
        onOpenChange={(o) => { if (!o) crud.closeModal(); }}
        title={crud.editing?.id ? 'Modifica visita medica' : 'Nuova visita medica'}
        fields={visitaFields(workers || [], companies || [], isClient)}
        initialValues={crud.editing || {}}
        onSubmit={crud.save}
      />
    </div>
  );
};

export default SorveglianzaPage;
